import React, { FC, useState, useEffect, useContext } from 'react';

import { Button, Grid, Typography } from '@mui/material';

import ErrorMessage from '../layout/ErrorMessage';
import SelectInput from '../form/components/SelectInput';
import TextInput from '../form/components/TextInput';
import LabeledToggleButton from '../form/components/LabeledToggleButton';
import LoadingWrapper from '../layout/LoadingWrapper';

import { SnackBarContext } from '../form/contexts/SnackBarContext';

import { titleize } from '../../utils/titleize';

import axios from '../utils/axios.utils';

interface UpdateFormProps {
  fields: any[];
  record: Record<any, any>;
  controller: string;
  modelName: string;
}

const UpdateForm: FC<UpdateFormProps> = (props) => {
  const { controller, modelName, fields, record } = props;

  const { setSnackBarOpen, setSnackBarMessage } = useContext(SnackBarContext);

  const [formValues, setFormValues] = useState<Record<any, any>>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (record?.id) {
      setFormValues(record);
    }
  }, [record]);

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response: any = await axios.put(`/${controller}/${record?.id}`, {
        [modelName]: formValues,
      });
      setFormValues(response.data.result);
      setSnackBarOpen(true);
      setSnackBarMessage('Record Updated');
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong updating the Record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid key={String(field.name)} item xs={field.xs ?? 12} {...field.gridProps}>
                {field.kind === 'custom' ? (
                  field.render({ formValues, setFormValues })
                ) : field.kind === 'text' ? (
                  <TextInput
                    label={titleize(field.name)}
                    value={formValues?.[field.name] ?? ''}
                    valueChanged={(value: string) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }))
                    }
                    shrinkLabel
                  />
                ) : field.kind === 'toggle' ? (
                  <>
                    <Typography variant="body1">{titleize(field.name)}</Typography>
                    <LabeledToggleButton
                      onLabel={field.onLabel ?? 'On'}
                      offLabel={field.offLabel ?? 'Off'}
                      param={field.name}
                      formValues={formValues ?? {}}
                      setFormValues={setFormValues}
                    />
                  </>
                ) : (
                  <SelectInput
                    label={titleize(field.name)}
                    choices={field.choices}
                    value={formValues?.[field.name]}
                    valueChanged={(val: string) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: val,
                      }))
                    }
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        <Grid item xs={12} textAlign="right">
          <Button variant="contained" style={{ marginTop: 15 }} onClick={handleUpdate}>
            Update
          </Button>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default UpdateForm;
